// ShadowDao — data access for the four fakturownia shadow tables.
// All Supabase queries live here; services never hand-write queries.

import type { SupabaseClient } from '../../base.ts';
import type {
  ShadowRow,
  ShadowRevisionInsert,
  KsefFindingRow,
  HelpdeskRequestInsert,
} from '@shared/types/fakturownia_shadow.ts';
import type { CapturedBankAccount } from './captureBankAccounts.ts';

// Direction type for cursor keys (matches ShadowSyncService usage).
export type ShadowSyncDirection = 'received' | 'issued';

// Row types are canonical in @shared/types/fakturownia_shadow.ts (matches the rest
// of shared/types/*). Consumers import them from there, not from this DAO.

// ── ShadowDao ────────────────────────────────────────────────────────────────

export class ShadowDao {
  constructor(private readonly sb: SupabaseClient) {}

  // ── fakturownia_shadow ──────────────────────────────────────────────────

  async getShadow(externalId: string): Promise<ShadowRow | null> {
    const res = await this.sb
      .from('fakturownia_shadow')
      .select('*')
      .eq('external_id', externalId)
      .maybeSingle();
    if (res.error) throw res.error;
    return res.data as ShadowRow | null;
  }

  /**
   * Upsert the primary shadow row (insert or replace on external_id conflict).
   * Used when checksum has changed → full content update + revision bump.
   */
  async upsertShadow(row: ShadowRow): Promise<void> {
    const { error } = await this.sb
      .from('fakturownia_shadow')
      .upsert(row as unknown as Record<string, unknown>, { onConflict: 'external_id' });
    if (error) throw error;
  }

  /**
   * Touch-only update: fakturownia_updated_at unchanged but we still want to record
   * the sync time. Only updates last_synced_at (no content/checksum/revision change).
   * Clears remote_missing_at — being seen in a remote fetch proves the invoice exists.
   */
  async touchShadow(externalId: string, lastSyncedAt: string): Promise<void> {
    const { error } = await this.sb
      .from('fakturownia_shadow')
      .update({ last_synced_at: lastSyncedAt, remote_missing_at: null })
      .eq('external_id', externalId);
    if (error) throw error;
  }

  /** Update verify-related fields after a KSeF check. */
  async updateVerifyStatus(
    externalId: string,
    verifyStatus: string,
    ksefCheckedAt: string,
  ): Promise<void> {
    const { error } = await this.sb
      .from('fakturownia_shadow')
      .update({ verify_status: verifyStatus, ksef_checked_at: ksefCheckedAt })
      .eq('external_id', externalId);
    if (error) throw error;
  }

  /** Persist the full parsed KSeF invoice (+ raw XML) on the shadow row. Called by the
   *  verification pass once the gov XML is fetched and parsed. */
  async storeKsefContent(
    externalId: string,
    ksefContent: Record<string, unknown>,
    ksefXml: string,
  ): Promise<void> {
    const { error } = await this.sb
      .from('fakturownia_shadow')
      .update({ ksef_content: ksefContent, ksef_xml: ksefXml })
      .eq('external_id', externalId);
    if (error) throw error;
  }

  /** Write the canonical references extracted from the KSeF invoice onto the linked
   *  `invoices` row (this DAO already resolves that row via getInvoiceIdByExternalId). */
  async setInvoiceExternalReferences(invoiceId: number, references: string[]): Promise<void> {
    const { error } = await this.sb
      .from('invoices')
      .update({ external_references: references })
      .eq('id', invoiceId);
    if (error) throw error;
  }

  /**
   * Record a failed verification attempt (no_xml / parse_error). Increments
   * verify_attempts, stores the reason, and flips verify_status to 'unverifiable'
   * once maxAttempts is reached — permanently removing the row from the pending
   * queue (M2: no more redundant gov-XML fetches every batch).
   */
  async recordVerifyFailure(
    externalId: string,
    currentAttempts: number,
    reason: string,
    ksefCheckedAt: string,
    maxAttempts: number,
  ): Promise<void> {
    const attempts = currentAttempts + 1;
    const { error } = await this.sb
      .from('fakturownia_shadow')
      .update({
        verify_attempts: attempts,
        last_verify_error: reason,
        verify_status: attempts >= maxAttempts ? 'unverifiable' : 'pending',
        ksef_checked_at: ksefCheckedAt,
      })
      .eq('external_id', externalId);
    if (error) throw error;
  }

  /**
   * Stamp remote_missing_at on rows of a direction that a completed FULL SWEEP did
   * NOT see (M4c). Semantics: the full sweep bypasses Gate 1, so every remotely
   * present row inside the sweep window gets last_synced_at bumped (touch or upsert).
   * A row whose fakturownia_updated_at falls inside the sweep window but whose
   * last_synced_at was NOT bumped this pass was absent from the complete remote
   * fetch → deleted in Fakturownia. Idempotent (already-stamped rows untouched).
   */
  async markRemoteMissing(
    direction: ShadowSyncDirection,
    sweepSince: string,
    staleBefore: string,
  ): Promise<string[]> {
    const { data, error } = await this.sb
      .from('fakturownia_shadow')
      .update({ remote_missing_at: new Date().toISOString() })
      .eq('direction', direction)
      .gte('fakturownia_updated_at', sweepSince)
      .lt('last_synced_at', staleBefore)
      .is('remote_missing_at', null)
      .select('external_id');
    if (error) throw error;
    return ((data ?? []) as { external_id: string }[]).map((r) => r.external_id);
  }

  /** Mark shadow as reconciled (all findings resolved or no findings). */
  async markReconciled(externalId: string, reconciledAt: string): Promise<void> {
    const { error } = await this.sb
      .from('fakturownia_shadow')
      .update({ app_reconciled_at: reconciledAt })
      .eq('external_id', externalId);
    if (error) throw error;
  }

  // ── Sync cursor (reuses sync_metadata — same store as InvoiceDao) ──────────

  /**
   * Get the cursor ISO timestamp for a shadow sync direction.
   * Keys: 'shadow_sync_received' | 'shadow_sync_issued' (stored in sync_metadata.sync_type).
   */
  async getCursor(direction: ShadowSyncDirection): Promise<string | null> {
    const res = await this.sb
      .from('sync_metadata')
      .select('last_sync_at')
      .eq('sync_type', `shadow_sync_${direction}`)
      .maybeSingle();
    if (res.error) throw res.error;
    const row = res.data as { last_sync_at?: string | null } | null;
    return row?.last_sync_at ?? null;
  }

  /**
   * Advance (or initialize) the cursor for a shadow sync direction.
   * Upserts into sync_metadata so first call creates the row.
   */
  async setCursor(direction: ShadowSyncDirection, iso: string): Promise<void> {
    const { error } = await this.sb
      .from('sync_metadata')
      .upsert(
        { sync_type: `shadow_sync_${direction}`, last_sync_at: iso, updated_at: new Date().toISOString() },
        { onConflict: 'sync_type' },
      );
    if (error) throw error;
  }

  /**
   * List shadows with verify_status='pending' that have a ksef_number (candidates for
   * verification). Excludes rows with verify_attempts ≥ maxAttempts (M2 — those are
   * flipped to 'unverifiable' by recordVerifyFailure; the filter here is belt-and-braces
   * so a stuck row can never dominate the stable-ordered batch again).
   */
  async listPendingVerification(limit = 100, maxAttempts = 5): Promise<ShadowRow[]> {
    const { data, error } = await this.sb
      .from('fakturownia_shadow')
      .select('*')
      .eq('verify_status', 'pending')
      .not('ksef_number', 'is', null)
      .lt('verify_attempts', maxAttempts)
      .order('last_synced_at', { ascending: true })
      .limit(limit);
    if (error) throw error;
    return (data as ShadowRow[]) ?? [];
  }

  /**
   * Fakturownia external ids whose note still carries the PENDING hold marker.
   *
   * The marker lives only in FA's `internal_note`, and the shadow already mirrors
   * that note — so the full sweep can discover every still-marked invoice for the
   * price of one DB query instead of a GET per invoice. Whether the hold is
   * actually released is decided downstream (the writer strips the keyword only
   * when payment_status is no longer 'on_hold'), so this deliberately over-selects
   * rather than duplicating that rule here.
   *
   * Returns external ids, not invoice ids: the shadow is keyed on `external_id`
   * and carries no FK to `invoices`.
   */
  async listPendingNoteExternalIds(): Promise<string[]> {
    // PAGED: an unranged select silently stops at PostgREST's ~1000-row default.
    const out: string[] = [];
    const pageSize = 1000;
    for (let from = 0; ; from += pageSize) {
      const { data, error } = await this.sb
        .from('fakturownia_shadow')
        .select('external_id')
        .ilike('content->>internal_note', '%PENDING%')
        .order('external_id', { ascending: true })
        .range(from, from + pageSize - 1);
      if (error) throw error;
      const rows = (data ?? []) as { external_id: string }[];
      out.push(...rows.map(r => r.external_id));
      if (rows.length < pageSize) break;
    }
    return out;
  }

  /**
   * Resolve the app `invoices.id` for a Fakturownia external id (minor-b: findings
   * previously always carried invoice_id=null, orphaning them from the domain).
   */
  async getInvoiceIdByExternalId(externalId: string): Promise<number | null> {
    const { data, error } = await this.sb
      .from('invoices')
      .select('id')
      .eq('external_system_id', externalId)
      .eq('is_deleted', false)
      .maybeSingle();
    if (error) throw error;
    return (data as { id: number } | null)?.id ?? null;
  }

  /**
   * Stored direction for a Fakturownia external id, or null when we have never seen it.
   *
   * The batch sync derives direction from which FA list endpoint (`income=0/1`) an id
   * arrived on, so it can never disagree with itself. The per-invoice path takes
   * direction as a parameter, which makes disagreement possible for the first time —
   * and direction drives the line-item sign (`normalize.ts`: received = -1) and is
   * overwritten on the shadow by `upsertShadow`'s conflict update. Callers use this to
   * refuse a direction that contradicts what we already hold.
   */
  async getStoredDirection(externalId: string): Promise<string | null> {
    const { data: shadow, error: shadowErr } = await this.sb
      .from('fakturownia_shadow')
      .select('direction')
      .eq('external_id', externalId)
      .maybeSingle();
    if (shadowErr) throw shadowErr;
    const shadowDirection = (shadow as { direction: string } | null)?.direction;
    if (shadowDirection) return shadowDirection;

    // No shadow yet — an invoice may still exist (created outside the shadow pipeline).
    const { data: invoice, error: invoiceErr } = await this.sb
      .from('invoices')
      .select('direction')
      .eq('external_system_id', externalId)
      .eq('is_deleted', false)
      .maybeSingle();
    if (invoiceErr) throw invoiceErr;
    return (invoice as { direction: string } | null)?.direction ?? null;
  }

  /**
   * Invoices whose `external_references` were never surfaced, keyset-paged by id.
   *
   * Keyset (not "select the head until it's empty"): an invoice whose shadow holds no
   * KSeF XML legitimately stays NULL — a later verify must still be able to fill it — so
   * a head-selecting query would return the same unfillable rows forever and the caller's
   * hasMore loop would never converge. `afterId` walks past them instead.
   */
  async listInvoicesMissingExternalReferences(
    afterId: number,
    limit: number,
  ): Promise<Array<{ id: number; external_system_id: string }>> {
    const { data, error } = await this.sb
      .from('invoices')
      .select('id, external_system_id')
      .is('external_references', null)
      .not('external_system_id', 'is', null)
      .eq('is_deleted', false)
      .gt('id', afterId)
      .order('id', { ascending: true })
      .limit(limit);
    if (error) throw error;
    return (data as Array<{ id: number; external_system_id: string }>) ?? [];
  }

  /**
   * Corrections that were never attached to the invoice they correct. Fakturownia
   * frequently delivers a vendor-issued correction with no parent id, and the only other
   * attach path runs when the PARENT is created — so these are orphaned indefinitely and
   * `prepareForMatching` (which merges corrections solely via `correction_of_invoice_id`)
   * never applies them.
   */
  async listOrphanCorrections(
    limit: number,
  ): Promise<Array<{ id: number; external_system_id: string; seller_company_id: number | null }>> {
    const { data, error } = await this.sb
      .from('invoices')
      .select('id, external_system_id, seller_company_id')
      .eq('invoice_type', 'correction')
      .is('correction_of_invoice_id', null)
      .not('external_system_id', 'is', null)
      .eq('is_deleted', false)
      .order('id', { ascending: true })
      .limit(limit);
    if (error) throw error;
    return (data as Array<{ id: number; external_system_id: string; seller_company_id: number | null }>) ?? [];
  }

  /** Single-row variant of {@link listOrphanCorrections} for the per-invoice deep-refresh
   *  path — the same orphan-correction shape, narrowed to one `invoices.id`. Null when the
   *  invoice isn't an unattached correction (already attached, not a correction, or missing
   *  its external id). */
  async getOrphanCorrection(
    invoiceId: number,
  ): Promise<{ id: number; external_system_id: string; seller_company_id: number | null } | null> {
    const { data, error } = await this.sb
      .from('invoices')
      .select('id, external_system_id, seller_company_id')
      .eq('invoice_type', 'correction')
      .is('correction_of_invoice_id', null)
      .not('external_system_id', 'is', null)
      .eq('is_deleted', false)
      .eq('id', invoiceId)
      .maybeSingle();
    if (error) throw error;
    return data as { id: number; external_system_id: string; seller_company_id: number | null } | null;
  }

  /** Resolve an invoice by its number WITHIN one vendor — numbers are only unique per
   *  vendor, so the vendor is part of the key, never an optional filter. */
  async findInvoiceByNumberAndVendor(
    invoiceNumber: string,
    sellerCompanyId: number | null,
  ): Promise<number | null> {
    if (sellerCompanyId == null) return null;
    const { data, error } = await this.sb
      .from('invoices')
      .select('id')
      .eq('invoice_number', invoiceNumber)
      .eq('seller_company_id', sellerCompanyId)
      .eq('is_deleted', false)
      .limit(2);
    if (error) throw error;
    const rows = (data as Array<{ id: number }>) ?? [];
    // Ambiguous number within one vendor → refuse rather than guess a parent.
    return rows.length === 1 ? rows[0].id : null;
  }

  /** Attach a correction to the invoice it corrects. */
  async setInvoiceCorrectionParent(correctionId: number, parentInvoiceId: number): Promise<void> {
    const { error } = await this.sb
      .from('invoices')
      .update({ correction_of_invoice_id: parentInvoiceId, invoice_type: 'correction' })
      .eq('id', correctionId);
    if (error) throw error;
  }

  // ── invoice_bank_accounts (capture) ─────────────────────────────────────

  /**
   * Replace the captured accounts of one invoice with exactly `rows`.
   *
   * Idempotent by construction: capture is re-derived from the same KSeF XML on every
   * re-verify, so this upserts on the (invoice_id, account_number) unique constraint and
   * then removes only the rows the invoice no longer presents. `created_at` therefore
   * survives a re-verify and keeps meaning "first seen on this invoice".
   *
   * Stale rows are deleted BY ID rather than by a `not.in` filter on account_number: an
   * unrecoverable account is captured verbatim (see captureBankAccounts), so the column
   * can hold arbitrary vendor text that a hand-built PostgREST filter string would have
   * to quote correctly. Ids are integers and supabase-js encodes the array itself.
   */
  async replaceInvoiceBankAccounts(
    invoiceId: number,
    rows: CapturedBankAccount[],
  ): Promise<void> {
    const { data: existing, error: readError } = await this.sb
      .from('invoice_bank_accounts')
      .select('id, account_number')
      .eq('invoice_id', invoiceId);
    if (readError) throw readError;

    if (rows.length > 0) {
      const { error: upsertError } = await this.sb
        .from('invoice_bank_accounts')
        .upsert(
          rows.map((r) => ({ ...r, invoice_id: invoiceId })),
          { onConflict: 'invoice_id,account_number' },
        );
      if (upsertError) throw upsertError;
    }

    const keep = new Set(rows.map((r) => r.account_number));
    const staleIds = ((existing as Array<{ id: number; account_number: string }> | null) ?? [])
      .filter((r) => !keep.has(r.account_number))
      .map((r) => r.id);
    if (staleIds.length === 0) return;

    const { error: deleteError } = await this.sb
      .from('invoice_bank_accounts')
      .delete()
      .in('id', staleIds);
    if (deleteError) throw deleteError;
  }

  /**
   * Invoices that have no captured accounts yet, keyset-paged by id.
   *
   * "Has no captured accounts" is expressed as a PostgREST embed on the
   * invoice_bank_accounts FK and filtered here — PostgREST cannot express the anti-join
   * itself. The cursor therefore advances by the last SELECTED id, not the last returned
   * candidate: an invoice whose shadow holds no KSeF XML is never capturable and would
   * otherwise be re-read on every pass forever.
   */
  async listInvoicesMissingBankAccounts(
    afterId: number,
    limit: number,
  ): Promise<{
    candidates: Array<{ id: number; external_system_id: string }>;
    lastSelectedId: number;
    selectedCount: number;
  }> {
    const { data, error } = await this.sb
      .from('invoices')
      .select('id, external_system_id, invoice_bank_accounts(id)')
      .not('external_system_id', 'is', null)
      .eq('is_deleted', false)
      .gt('id', afterId)
      .order('id', { ascending: true })
      .limit(limit);
    if (error) throw error;

    const page = (data as Array<{
      id: number;
      external_system_id: string;
      invoice_bank_accounts: Array<{ id: number }> | null;
    }> | null) ?? [];

    return {
      candidates: page
        .filter((r) => (r.invoice_bank_accounts?.length ?? 0) === 0)
        .map((r) => ({ id: r.id, external_system_id: r.external_system_id })),
      lastSelectedId: page.length > 0 ? page[page.length - 1].id : afterId,
      selectedCount: page.length,
    };
  }

  /** Stored KSeF XML for the given external ids (rows without XML are omitted). */
  async listShadowXmlByExternalIds(
    externalIds: string[],
  ): Promise<Array<{ external_id: string; ksef_xml: string }>> {
    if (externalIds.length === 0) return [];
    const { data, error } = await this.sb
      .from('fakturownia_shadow')
      .select('external_id, ksef_xml')
      .in('external_id', externalIds)
      .not('ksef_xml', 'is', null);
    if (error) throw error;
    return (data as Array<{ external_id: string; ksef_xml: string }>) ?? [];
  }

  /**
   * List shadows of a direction whose synced revision is ahead of the revision
   * last projected into `invoices` (needs_projection generated column = true).
   * Oldest sync first so projection drains the backlog in order.
   */
  async listPendingProjection(direction: ShadowSyncDirection, limit = 200): Promise<ShadowRow[]> {
    const { data, error } = await this.sb
      .from('fakturownia_shadow')
      .select('*')
      .eq('direction', direction)
      .eq('needs_projection', true)
      .order('last_synced_at', { ascending: true })
      .limit(limit);
    if (error) throw error;
    return (data as ShadowRow[]) ?? [];
  }

  /** Record that a shadow has been projected into `invoices` up to `revision`. */
  async markProjected(externalId: string, revision: number): Promise<void> {
    const { error } = await this.sb
      .from('fakturownia_shadow')
      .update({ app_projected_revision: revision })
      .eq('external_id', externalId);
    if (error) throw error;
  }

  /** List shadows with open findings (for reconcile pass). */
  async listWithOpenFindings(limit = 100): Promise<ShadowRow[]> {
    const { data: findingData, error: fErr } = await this.sb
      .from('invoice_ksef_finding')
      .select('external_id')
      .eq('status', 'open')
      .limit(limit);
    if (fErr) throw fErr;
    const ids = [...new Set((findingData ?? []).map((r: { external_id: string }) => r.external_id))];
    if (ids.length === 0) return [];
    const { data, error } = await this.sb
      .from('fakturownia_shadow')
      .select('*')
      .in('external_id', ids);
    if (error) throw error;
    return (data as ShadowRow[]) ?? [];
  }

  // ── fakturownia_shadow_revision ────────────────────────────────────────

  /** Append a new revision entry (append-only). */
  async appendRevision(rev: ShadowRevisionInsert): Promise<void> {
    const { error } = await this.sb
      .from('fakturownia_shadow_revision')
      .insert(rev as unknown as Record<string, unknown>);
    if (error) throw error;
  }

  // ── invoice_ksef_finding ───────────────────────────────────────────────

  /** Fetch open findings for a given external_id. */
  async getOpenFindings(externalId: string): Promise<KsefFindingRow[]> {
    const { data, error } = await this.sb
      .from('invoice_ksef_finding')
      .select('*')
      .eq('external_id', externalId)
      .eq('status', 'open');
    if (error) throw error;
    return (data as KsefFindingRow[]) ?? [];
  }

  /**
   * Upsert a finding by (external_id, field). If a finding with the same key
   * already exists it is updated (not duplicated).
   */
  async upsertFinding(finding: Omit<KsefFindingRow, 'id' | 'detected_at' | 'resolved_at'>): Promise<void> {
    // Select-then-insert/update on (external_id, field) WHERE status='open'.
    // A partial unique index (idx_invoice_ksef_finding_open_unique) backs this:
    // the normal path is handled here; the index guards the concurrent-sweep race.
    const selRes = await this.sb
      .from('invoice_ksef_finding')
      .select('id, attempts')
      .eq('external_id', finding.external_id)
      .eq('field', finding.field)
      .eq('status', 'open')
      .maybeSingle();
    if (selRes.error) throw selRes.error;
    const existing = selRes.data as { id: number; attempts: number | null } | null;

    if (existing) {
      const { error } = await this.sb
        .from('invoice_ksef_finding')
        .update({
          fa_value: finding.fa_value,
          ksef_value: finding.ksef_value,
          attempts: (existing.attempts ?? 0) + 1,
          escalate_after: finding.escalate_after,
          invoice_id: finding.invoice_id,
          payload: finding.payload ?? null,
        })
        .eq('id', existing.id);
      if (error) throw error;
    } else {
      const { error } = await this.sb
        .from('invoice_ksef_finding')
        .insert(finding as Record<string, unknown>);
      if (error) throw error;
    }
  }

  /** Mark a finding as resolved. */
  async resolveFinding(findingId: number, resolvedAt: string): Promise<void> {
    const { error } = await this.sb
      .from('invoice_ksef_finding')
      .update({ status: 'resolved', resolved_at: resolvedAt })
      .eq('id', findingId);
    if (error) throw error;
  }

  /** Mark a finding as escalated and link to a helpdesk request. */
  async escalateFinding(findingId: number, helpdeskRequestId: number): Promise<void> {
    const { error } = await this.sb
      .from('invoice_ksef_finding')
      .update({ status: 'escalated', helpdesk_request_id: helpdeskRequestId })
      .eq('id', findingId);
    if (error) throw error;
  }

  // ── fakturownia_helpdesk_request ───────────────────────────────────────

  /** Create a helpdesk request and return its ID. */
  async createHelpdeskRequest(req: HelpdeskRequestInsert): Promise<number> {
    const { data, error } = await this.sb
      .from('fakturownia_helpdesk_request')
      .insert(req as unknown as Record<string, unknown>)
      .select('id')
      .single();
    if (error) throw error;
    return (data as { id: number }).id;
  }
}
